import styled, { css } from "styled-components";

import { InputProps } from "./Input";
import { InputMode, InputSize } from "./types";
import { BoxSizingStyle } from "../BoxSizingStyle";
import { FontStyle } from "../FontStyle";
import { InputResetStyle } from "../InputResetStyle";

interface StyledInputWrapperProps {
  $size?: InputSize;
  $invalid?: boolean;
  $disabled?: boolean;
  $mode?: InputMode;
}

export const StyledInputWrapper = styled.div<StyledInputWrapperProps>`
  align-items: center;
  background-color: var(--input-background-color);
  border-color: var(--color-theme-500);
  border-radius: 8px;
  border-style: solid;
  border-width: 1px;
  cursor: text;
  display: flex;
  height: 32px;
  padding-block: 4px;
  padding-inline: 4px;
  transition: all 0.3s ease;
  width: 360px;

  ${FontStyle}
  ${BoxSizingStyle}


  ${(props) =>
    props.$mode === "flat" &&
    css`
      background-color: transparent;
      border: none;
      border-radius: none;
    `}


  ${(props) =>
    props.$size === "small" &&
    css`
      border-radius: 6px;
      height: 24px;
    `}

  ${(props) =>
    props.$size === "big" &&
    css`
      height: 40px;
    `}

    ${(props) =>
    props.$size === "biggest" &&
    css`
      height: 48px;
    `}

  ${(props) =>
    props.$disabled &&
    css`
      cursor: default;
      opacity: 50%;
    `}

  ${(props) =>
    !props.$disabled &&
    !props.$invalid &&
    css`
      &:focus-within,
      &:hover {
        border-color: var(--color-primary);
      }
    `}

  ${(props) =>
    !props.$disabled &&
    props.$invalid &&
    props.$mode === "outlined" &&
    css`
      border-color: var(--red-alert);
    `}
`;

StyledInputWrapper.displayName = "StyledInputWrapper";

interface StyledInputProps extends Pick<InputProps, "align"> {
  $size?: InputSize;
  $loading?: boolean;
  $mode?: InputMode;
  $invalid?: boolean;
}

export const StyledInput = styled.input<StyledInputProps>`
  ${BoxSizingStyle}
  ${InputResetStyle}
  background-color: var(--input-background-color);
  border: none;
  color: var(--color-theme-900);
  /* @TODO: Prebaciti velicine fontova, weight, razmake, itd... u naše varijable. */
  font-size: 0.875rem;
  font-weight: 400;
  letter-spacing: 0.02em;
  margin-block: 0;
  margin-inline: 4px;
  outline: none;
  padding: 0;
  width: 100%;

  ${(props) =>
    props.$mode === "flat" &&
    css`
      background-color: transparent;
    `}

  &::placeholder {
    color: var(--color-theme-transparent-500);
  }

  ${(props) =>
    props.$size === "big" &&
    css`
      font-size: 1rem;
    `}

  ${(props) =>
    props.$size === "biggest" &&
    css`
      font-size: 1.25rem;
      font-weight: 700;
    `}

  ${(props) =>
    props.disabled &&
    css`
      cursor: default;
    `}

  ${(props) =>
    props.$loading &&
    css`
      cursor: progress;
    `}

  ${(props) =>
    props.align &&
    css`
      text-align: ${props.align};
    `}

    ${(props) =>
    props.$mode === "flat" &&
    props.$invalid &&
    css`
      color: var(--red-alert);
    `}
`;

StyledInput.displayName = "StyledInput";

export const StyledPasswordIndicatorWrapper = styled.div`
  display: flex;
  gap: 2px;
  margin-top: 6px;
  margin-bottom: 4px;
`;

StyledPasswordIndicatorWrapper.displayName = "StyledPasswordIndicatorWrapper";

export const StyledPasswordIndicator = styled.div<{
  $strength: number;
  $index: number;
}>`
  width: 100%;
  height: 4px;
  border-radius: 6px;
  background-color: ${(props) => {
    const { $strength, $index } = props;

    if ($strength === 0) {
      return "var(--color-theme-300)";
    } else if ($strength === 1) {
      return $index === 0 ? "var(--red-alert)" : "var(--color-theme-300)";
    } else if ($strength === 2) {
      return $index < 2 ? "var(--color-orange)" : "var(--color-theme-300)";
    } else if ($strength === 3) {
      return $index < 3 ? "var(--color-blue-sky)" : "var(--color-theme-300)";
    } else {
      return "var(--color-sucess-green)";
    }
  }};
`;

StyledPasswordIndicator.displayName = "StyledPasswordIndicator";

export const StyledInputSelectTrigger = styled.div<{
  $size: string;
  $disabled?: boolean;
}>`
  display: flex;
  gap: 6px;
  align-items: center;
  margin-right: 2px;
  margin-left: 8px;
  color: var(--color-theme-700);
  font-weight: 400;
  letter-spacing: 0.02em;
  font-size: 0.875rem;
  flex-shrink: 0;
  cursor: ${(props) => {
    if (props.$disabled) {
      return "unset";
    }
    return "pointer";
  }};

  pointer-events: ${(props) => {
    if (props.$disabled) {
      return "none";
    }
    return "unset";
  }};

  ${(props) =>
    props.$size === "big" &&
    css`
      font-size: 1rem;
    `}
`;

StyledInputSelectTrigger.displayName = "StyledInputSelectTrigger";

export const StyledFlagTriggerLabel = styled.span<{
  $size: "small" | "regular" | "big";
}>`
  font-size: ${({ $size }) => ($size === "small" ? "20px" : "24px")};
`;

StyledFlagTriggerLabel.displayName = "StyledFlagTriggerLabel";

export const StyledOptionFlag = styled.span`
  font-size: 24px;
  margin-right: 4px;
`;

StyledFlagTriggerLabel.displayName = "StyledOptionFlag";

export const StyledOptionName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

StyledOptionName.displayName = "StyledOptionName";

export const StyledOptionWrapper = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 8px;
  display: flex;
  justify-items: center;
  align-items: center;
`;

StyledOptionWrapper.displayName = "StyledOptionWrapper";
